import React, { useState, useEffect, useRef } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Button,
    Alert,
    Chip,
    Checkbox,
    Divider,
    Autocomplete,
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    MergeType as MergeIcon,
    LinkOff as UnlinkIcon,
    Search as SearchIcon,
    AutoAwesome as SuggestIcon,
} from '@mui/icons-material';
import cityService from '../../services/cityService';

/**
 * Agrupación de localidades de reparto.
 *
 * En Tango la localidad es texto libre, así que un mismo pueblo llega escrito de
 * muchas formas ('A.G.CHAVES', 'AD GONZALEZ CHAVES'). Cada ciudad de reparto es
 * una agrupación: la ciudad canónica más las variantes asociadas a ella. Las
 * variantes se guardan como alias para que la sincronización nocturna no vuelva
 * a crearlas como ciudades independientes.
 */
const CityDuplicatesSection = () => {
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);

    const [cityGroups, setCityGroups] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [search, setSearch] = useState('');

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [openGroups, setOpenGroups] = useState({});
    const [openSuggestions, setOpenSuggestions] = useState({});
    const [showSuggestions, setShowSuggestions] = useState(false);
    // Selección por sugerencia: qué variantes entran y cuál es la canónica.
    const [selection, setSelection] = useState({});

    const containerRef = useRef(null);

    // El scroll lo maneja un contenedor interno de PageContainer, no la ventana:
    // para conservar la posición hay que ubicar ese ancestro desplazable.
    const getScrollParent = (node) => {
        let current = node?.parentElement;
        while (current) {
            const { overflowY } = window.getComputedStyle(current);
            if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    };

    // Conserva la posición de scroll a través de una recarga de datos, para que
    // agrupar o desasociar no devuelva la vista al principio de la lista.
    const preservingScroll = async (action) => {
        const scroller = getScrollParent(containerRef.current);
        const previous = scroller?.scrollTop;
        await action();
        if (scroller && previous != null) {
            requestAnimationFrame(() => { scroller.scrollTop = previous; });
        }
    };

    const fetchGroups = async (term = search) => {
        const groups = await cityService.getCityGroups(term);
        setCityGroups(groups);
    };

    const fetchSuggestions = async () => {
        const data = await cityService.getDuplicateSuggestions();
        const keyed = data.map((g) => ({ ...g, key: g.suggested_target_id }));
        setSuggestions(keyed);
        setSelection((prev) => {
            const next = { ...prev };
            keyed.forEach((group) => {
                next[group.key] = {
                    targetId: group.suggested_target_id,
                    checked: group.cities.map((c) => c.id),
                };
            });
            return next;
        });
    };

    const fetchAll = async () => {
        try {
            setLoading(true);
            setError('');
            await Promise.all([fetchGroups(), fetchSuggestions()]);
        } catch (err) {
            setError('Error al obtener las localidades.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (expanded && cityGroups.length === 0 && !loading) {
            fetchAll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    // Búsqueda con rebote: filtra en el servidor por nombre y por variante.
    useEffect(() => {
        if (!expanded) return undefined;
        const timer = setTimeout(() => {
            fetchGroups(search).catch(() => setError('Error al buscar localidades.'));
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // --- Agrupaciones -------------------------------------------------------

    const handleAddVariant = async (city, variant) => {
        if (!variant) return;
        await preservingScroll(async () => {
            try {
                setBusy(`group-${city.id}`);
                setError('');
                const result = await cityService.mergeCities(city.id, [variant.id]);
                setSuccess(`"${variant.name}" quedó asociada a "${result.target_name}". ${result.moved_clients} cliente(s) reasignado(s).`);
                await Promise.all([fetchGroups(), fetchSuggestions()]);
            } catch (err) {
                setError(err.response?.data?.error || 'Error al agrupar la localidad.');
            } finally {
                setBusy(null);
            }
        });
    };

    const handleUnlink = async (alias) => {
        await preservingScroll(async () => {
            try {
                setBusy(`alias-${alias.id}`);
                setError('');
                const result = await cityService.deleteAlias(alias.id);
                setSuccess(
                    `"${result.restored_city_name}" volvió a ser una localidad independiente. ` +
                    `${result.moved_clients} cliente(s) reasignado(s).`
                );
                await Promise.all([fetchGroups(), fetchSuggestions()]);
            } catch (err) {
                setError(err.response?.data?.error || 'Error al desasociar la variante.');
            } finally {
                setBusy(null);
            }
        });
    };

    // --- Sugerencias automáticas -------------------------------------------

    const toggleSuggestionCity = (key, cityId) => {
        const current = selection[key];
        const checked = current.checked.includes(cityId)
            ? current.checked.filter((id) => id !== cityId)
            : [...current.checked, cityId];
        const targetId = checked.includes(current.targetId) ? current.targetId : checked[0];
        setSelection((prev) => ({ ...prev, [key]: { checked, targetId } }));
    };

    const handleMergeSuggestion = async (key) => {
        const current = selection[key];
        const sourceIds = current.checked.filter((id) => id !== current.targetId);
        if (sourceIds.length === 0) {
            setError('Seleccioná al menos una variante además de la que se conserva.');
            return;
        }

        await preservingScroll(async () => {
            try {
                setBusy(`sug-${key}`);
                setError('');
                const result = await cityService.mergeCities(current.targetId, sourceIds);
                setSuccess(`Se agruparon ${result.merged_count} variantes en "${result.target_name}". ${result.moved_clients} cliente(s) reasignado(s).`);
                await Promise.all([fetchGroups(), fetchSuggestions()]);
            } catch (err) {
                setError(err.response?.data?.error || 'Error al agrupar las localidades.');
            } finally {
                setBusy(null);
            }
        });
    };

    const groupedCount = cityGroups.filter((g) => g.alias_count > 0).length;
    // Cualquier ciudad puede sumarse como variante de otra.
    const variantOptions = (city) => cityGroups.filter((c) => c.id !== city.id);

    return (
        <Accordion expanded={expanded} onChange={(e, isExpanded) => setExpanded(isExpanded)} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MergeIcon sx={{ color: '#4e7397' }} />
                    <Typography sx={{ fontWeight: 600 }}>Agrupación de Localidades</Typography>
                    {suggestions.length > 0 && (
                        <Chip
                            size="small"
                            label={`${suggestions.length} sugerencia(s)`}
                            sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }}
                        />
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails ref={containerRef}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    En Tango la localidad se carga como texto libre, por eso un mismo pueblo aparece
                    escrito de varias formas. Cada ciudad de acá abajo es una agrupación: podés sumarle
                    variantes o desasociarlas. Los clientes y las paradas de recorrido siguen a la
                    ciudad canónica, y la sincronización nocturna respeta lo que agrupes.
                </Alert>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && (
                    <>
                        {/* Sugerencias automáticas */}
                        {suggestions.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Button
                                    size="small"
                                    startIcon={<SuggestIcon />}
                                    onClick={() => setShowSuggestions((v) => !v)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {showSuggestions ? 'Ocultar' : 'Ver'} sugerencias automáticas ({suggestions.length})
                                </Button>

                                {showSuggestions && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', mb: 1 }}>
                                            Detectadas por parecido de nombre. Revisalas antes de aplicar:
                                            pueden faltar variantes o sobrar alguna que no corresponda.
                                        </Typography>
                                        {suggestions.map((group) => {
                                            const key = group.key;
                                            const current = selection[key] || { targetId: null, checked: [] };
                                            return (
                                                <Accordion
                                                    key={key}
                                                    expanded={!!openSuggestions[key]}
                                                    onChange={(e, isOpen) => setOpenSuggestions((p) => ({ ...p, [key]: isOpen }))}
                                                    sx={{ mb: 0.5, '&:before': { display: 'none' }, border: '1px solid #ffe0b2' }}
                                                >
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                                                {group.cities.find((c) => c.id === current.targetId)?.name || group.cities[0].name}
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={`${group.cities.length} variantes`}
                                                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100' }}
                                                            />
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <RadioGroup
                                                            value={current.targetId || ''}
                                                            onChange={(e) => setSelection((p) => ({ ...p, [key]: { ...p[key], targetId: Number(e.target.value) } }))}
                                                        >
                                                            {group.cities.map((city) => (
                                                                <Box key={city.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={current.checked.includes(city.id)}
                                                                        onChange={() => toggleSuggestionCity(key, city.id)}
                                                                    />
                                                                    <FormControlLabel
                                                                        value={city.id}
                                                                        control={<Radio size="small" />}
                                                                        disabled={!current.checked.includes(city.id)}
                                                                        label={
                                                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                                                {city.name}{' '}
                                                                                <Typography component="span" sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                                                                    ({city.client_count ?? 0} cli)
                                                                                </Typography>
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </RadioGroup>
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<MergeIcon />}
                                                                disabled={busy === `sug-${key}` || current.checked.length < 2}
                                                                onClick={() => handleMergeSuggestion(key)}
                                                            >
                                                                {busy === `sug-${key}` ? 'Agrupando...' : 'Agrupar'}
                                                            </Button>
                                                        </Box>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Box>
                                )}
                                <Divider sx={{ mt: 2 }} />
                            </Box>
                        )}

                        {/* Buscador */}
                        <TextField
                            size="small"
                            fullWidth
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar localidad o variante..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#4e7397' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 1.5 }}
                        />

                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', mb: 1 }}>
                            {cityGroups.length} localidad(es) de reparto · {groupedCount} con variantes agrupadas
                        </Typography>

                        {cityGroups.length === 0 && (
                            <Typography sx={{ color: '#4e7397', fontSize: '0.875rem', py: 2 }}>
                                No se encontraron localidades.
                            </Typography>
                        )}

                        {/* Agrupaciones: todas las ciudades de reparto */}
                        {cityGroups.map((city) => (
                            <Accordion
                                key={city.id}
                                expanded={!!openGroups[city.id]}
                                onChange={(e, isOpen) => setOpenGroups((p) => ({ ...p, [city.id]: isOpen }))}
                                sx={{
                                    mb: 0.5,
                                    '&:before': { display: 'none' },
                                    border: '1px solid #e0e0e0',
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                                            {city.name}
                                        </Typography>
                                        {city.alias_count > 0 && (
                                            <Chip
                                                size="small"
                                                label={`${city.alias_count} variante(s)`}
                                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                                            />
                                        )}
                                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                            {city.client_count} clientes
                                        </Typography>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails>
                                    {city.aliases.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                                            {city.aliases.map((alias) => (
                                                <Tooltip key={alias.id} title="Desasociar: vuelve a ser una localidad independiente">
                                                    <Chip
                                                        label={alias.raw_name}
                                                        size="small"
                                                        onDelete={() => handleUnlink(alias)}
                                                        deleteIcon={<UnlinkIcon />}
                                                        disabled={busy === `alias-${alias.id}`}
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.8125rem', color: '#4e7397', mb: 1.5 }}>
                                            Sin variantes asociadas.
                                        </Typography>
                                    )}

                                    <Autocomplete
                                        size="small"
                                        options={variantOptions(city)}
                                        getOptionLabel={(o) => o.name}
                                        value={null}
                                        disabled={busy === `group-${city.id}`}
                                        onChange={(e, value) => handleAddVariant(city, value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={
                                                    busy === `group-${city.id}`
                                                        ? 'Agrupando...'
                                                        : `Asociar otra localidad a "${city.name}"...`
                                                }
                                            />
                                        )}
                                    />
                                    <Typography sx={{ fontSize: '0.7rem', color: '#4e7397', mt: 0.5 }}>
                                        La localidad elegida se absorbe en "{city.name}": sus clientes y paradas pasan acá.
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default CityDuplicatesSection;
